<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/Base/BaseButton.vue'
import BaseSlidingModal from '@/Old/components/BaseSlidingModal.vue'

import type { ErrorResponse } from '@/types'

const props = defineProps<{
  locale: string
  errorText: ErrorResponse | null
  modelValue: boolean
}>()

const { t } = useI18n()
const emit = defineEmits(['update:modelValue'])

const daysUntilParticipations = computed(() => {
  return props.errorText?.data.message.match(/\((\d+)\)/)?.[1] || ''
})

const messageError = computed(() => {
  const message = props.errorText?.data.message.split(' ')[0]
  return message || props.errorText?.data.message || ''
})

const participatedToday = computed(() => {
  return (
    messageError.value === 'HasAlreadyParticipatedWithinRangeException' &&
    daysUntilParticipations.value === '1'
  )
})

const alreadyParticipated = computed(() => {
  return messageError.value === 'HasAlreadyParticipatedException'
})

const alreadyParticipatedWithinRange = computed(() => {
  return (
    messageError.value === 'HasAlreadyParticipatedWithinRangeException' &&
    daysUntilParticipations.value !== '1'
  )
})

const title = computed(() => {
  if (participatedToday.value) {
    return t('errors.participatedToday.title', props.locale)
  }
  if (alreadyParticipated.value) {
    return t('errors.alreadyParticipated.title', props.locale)
  }
  if (alreadyParticipatedWithinRange.value) {
    return t('errors.alreadyParticipatedWithinRange.title', props.locale)
  }
  return t('errors.unknown')
})

const content = computed(() => {
  if (participatedToday.value) {
    return t('errors.participatedToday.content', props.locale)
  }
  if (alreadyParticipated.value) {
    return t('errors.alreadyParticipated.content', props.locale)
  }
  if (alreadyParticipatedWithinRange.value) {
    return t('errors.alreadyParticipatedWithinRange.content', {
      locale: props.locale,
      daysUntilParticipations: daysUntilParticipations.value,
    })
  }
  return ''
})

const closeModal = () => {
  emit('update:modelValue', false)
}
</script>

<template>
  <base-sliding-modal :model-value="modelValue">
    <template #content>
      <div class="flex flex-col items-center justify-center text-center">
        <h1 class="text-2xl font-bold text-gray-700">
          {{ title }}
        </h1>
        <p class="my-4 text-gray-700">
          {{ content }}
        </p>
        <base-button v-if="alreadyParticipatedWithinRange" @click="closeModal">
          {{ $t('buttons.tryAgain', locale) }}
        </base-button>
      </div>
    </template>
  </base-sliding-modal>
</template>
